import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react';
import type { ReactNode } from 'react';
import { resolveLocale } from './resolveLocale';
import type { Locale } from './types';

interface LocaleContextValue {
    locale: Locale;
    setInitLang: (lang: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
    const [initLang, setInitLangState] = useState<string | undefined>(
        undefined
    );

    const locale = useMemo(() => resolveLocale(initLang), [initLang]);

    const setInitLang = useCallback((lang: string) => {
        setInitLangState(lang);
    }, []);

    const value = useMemo<LocaleContextValue>(
        () => ({ locale, setInitLang }),
        [locale, setInitLang]
    );

    return (
        <LocaleContext.Provider value={value}>
            {children}
        </LocaleContext.Provider>
    );
}

/**
 * Returns the active locale. Falls back to `'en'` when used outside a
 * `LocaleProvider` (e.g. in isolated unit tests).
 */
export function useLocale(): Locale {
    return useContext(LocaleContext)?.locale ?? 'en';
}

/**
 * Returns the `setInitLang` setter. Returns a no-op when used outside a
 * `LocaleProvider`.
 */
export function useSetInitLang(): (lang: string) => void {
    return useContext(LocaleContext)?.setInitLang ?? (() => undefined);
}
